import { NavLink } from "react-router-dom";
import { Building2, LayoutDashboard, Home, Wallet, Car, Users, Shield, LogOut, UserCog, Lock, CreditCard, Package, BadgePercent, CalendarClock, ScrollText, ShieldCheck, KeyRound, Map as MapIcon, Camera, Plane, ScanLine, FileSignature, Landmark, Settings, X, CalendarDays, ListTodo, BarChart3, LifeBuoy, Globe, Activity, PanelLeftClose, PanelLeftOpen, Share2, SlidersHorizontal, DatabaseBackup, BookOpen, LandPlot, FileCheck2 } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, ROLE_COLOR, PACKAGE_STYLE } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { MenuEditor, applyPrefs } from "@/components/MenuEditor";

const ALL = ["BROKER", "ACCOUNTANT", "CONSULTANT"];
const PLATFORM = ["SUPER_ADMIN", "REPRESENTATIVE", "PLATFORM_ACCOUNTANT"];
const MENU = [
  { to: "/panel", icon: LayoutDashboard, label: "Genel Bakış", roles: [...ALL, ...PLATFORM] },
  { to: "/admin/cms", icon: Globe, label: "Ana Sayfa Yönetimi", roles: ["SUPER_ADMIN"] },
  { to: "/admin/monitoring", icon: Activity, label: "Sistem İzleme", roles: ["SUPER_ADMIN"] },
  { to: "/offices", icon: Building2, label: "Emlak Ofisleri", roles: PLATFORM, perm: "OFFICE_VIEW" },
  { to: "/admin/subscriptions", icon: CalendarClock, label: "Abonelik Takibi", roles: PLATFORM, perm: "SUBSCRIPTION_VIEW" },
  { to: "/admin/finance", icon: Wallet, label: "Platform Finans", roles: PLATFORM, perm: "FINANCE_REPORTS" },
  { to: "/admin/fin", icon: BarChart3, label: "Finans Merkezi", roles: PLATFORM, perm: "FIN_VIEW" },
  { to: "/ops/drone", icon: Plane, label: "Drone Operasyon", roles: ["SUPER_ADMIN", "REPRESENTATIVE"], perm: "DRONE_OPS" },
  { to: "/admin/drone-plus", icon: Settings, label: "Drone Hizmet Ayarları", roles: PLATFORM, perm: "DRONE_MANAGE" },
  { to: "/admin/packages", icon: Package, label: "Paketler", roles: PLATFORM, perm: "PACKAGES_MANAGE" },
  { to: "/admin/campaigns", icon: BadgePercent, label: "Kampanyalar", roles: PLATFORM, perm: "CAMPAIGNS_MANAGE" },
  { to: "/admin/drone", icon: Plane, label: "Drone Talepleri", roles: PLATFORM, perm: "DRONE_MANAGE" },
  { to: "/admin/logs", icon: ScrollText, label: "İşlem & IP Kayıtları", roles: PLATFORM, perm: "LOGS_VIEW" },
  { to: "/admin/staff", icon: ShieldCheck, label: "Yetkili Hesaplar", roles: ["SUPER_ADMIN"] },
  { to: "/admin/support", icon: LifeBuoy, label: "Destek", roles: PLATFORM, perm: "SUPPORT_MANAGE" },
  { to: "/admin/settings", icon: Settings, label: "Ayarlar", roles: PLATFORM },
  { to: "/admin/data-access", icon: KeyRound, label: "Sınırlı Veri Erişimi", roles: ["SUPER_ADMIN"] },
  { to: "/admin/pool", icon: Share2, label: "CRM Havuzu Denetimi", roles: ["SUPER_ADMIN"] },
  { to: "/admin/backups", icon: DatabaseBackup, label: "Yedekleme", roles: ["SUPER_ADMIN"] },
  { to: "/admin/glossary", icon: BookOpen, label: "Sözlük & Rehber Yönetimi", roles: ["SUPER_ADMIN"] },
  { to: "/admin/legal", icon: FileCheck2, label: "Sözleşme ve Belgeler", roles: ["SUPER_ADMIN"] },
  { to: "/parcels", icon: LandPlot, label: "Parsel Sorgu", roles: [...ALL, ...PLATFORM] },
  { to: "/portfolios", icon: Home, label: "Portföy", roles: ALL, feature: "PORTFOLIO" },
  { to: "/calendar", icon: CalendarDays, label: "Takvim", roles: ALL },
  { to: "/todos", icon: ListTodo, label: "Yapılacaklar", roles: ALL },
  { to: "/reports", icon: BarChart3, label: "Raporlar", roles: ALL, feature: "PERFORMANCE" },
  { to: "/map", icon: MapIcon, label: "Harita", roles: ALL, feature: "MAP" },
  { to: "/shoots", icon: Camera, label: "Çekim Günü", roles: ALL, feature: "SHOOTS" },
  { to: "/drone", icon: Plane, label: "Drone Çekim", roles: ["BROKER", "CONSULTANT"], feature: "DRONE" },
  { to: "/scans", icon: ScanLine, label: "Belge Tarama", roles: ALL, feature: "SCAN" },
  { to: "/contracts", icon: FileSignature, label: "Sözleşmeler", roles: ALL, feature: "CONTRACT" },
  { to: "/deeds", icon: Landmark, label: "Tapu Takip", roles: ALL, feature: "DEED" },
  { to: "/finance", icon: Wallet, label: "Gelir / Gider", roles: ["BROKER", "ACCOUNTANT"], feature: "FINANCE" },
  { to: "/vehicles", icon: Car, label: "Araç Takibi", roles: ALL, feature: "VEHICLES" },
  { to: "/customers", icon: Users, label: "Müşteriler (CRM)", roles: ALL, feature: "CRM" },
  { to: "/pool", icon: Share2, label: "CRM Havuzu", roles: ALL },
  { to: "/users", icon: UserCog, label: "Kullanıcı Yönetimi", roles: ["BROKER", "SUPER_ADMIN"] },
  { to: "/subscription", icon: CreditCard, label: "Aboneliğim", roles: ["BROKER", "ACCOUNTANT"] },
  { to: "/data-access", icon: KeyRound, label: "Veri Erişim Talepleri", roles: ["BROKER"] },
  { to: "/backups", icon: DatabaseBackup, label: "Yedeklerim", roles: ["BROKER"] },
  { to: "/dictionary", icon: BookOpen, label: "Emlak Sözlüğü & Rehber", roles: [...ALL, ...PLATFORM] },
  { to: "/agreements", icon: FileCheck2, label: "Sözleşmelerim ve Onaylarım", roles: ALL },
  { to: "/settings", icon: Settings, label: "Ayarlar", roles: ["BROKER"] },
];

const testId = (to) => (to === "/panel" ? "nav-dashboard" : `nav-${to.replace(/^\//, "").replace(/\//g, "-")}`);

export const Brand = ({ compact }) => (
  <div className="flex items-center gap-2.5 min-w-0">
    <div className="w-9 h-9 shrink-0 rounded-xl bg-emerald-700 shadow-[0_6px_16px_-6px_rgba(4,120,87,0.6)] flex items-center justify-center">
      <Building2 className="w-4 h-4 text-white" />
    </div>
    {!compact && (
      <div className="min-w-0">
        <div className="font-bold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>EmlakPlus</div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Yönetim Paneli</div>
      </div>
    )}
  </div>
);

export function DesktopSidebar({ collapsed, onToggle }) {
  return (
    <div className="hidden lg:block sticky top-0 h-screen p-3 pr-0 shrink-0 z-30">
      <aside data-testid="desktop-sidebar" data-collapsed={collapsed}
        className={`glass-panel h-full rounded-2xl flex flex-col transition-[width] duration-300 ${collapsed ? "w-[76px]" : "w-64"}`}>
        <SidebarContent collapsed={collapsed} onToggle={onToggle} />
      </aside>
    </div>
  );
}

export function MobileDrawer({ open, onClose }) {
  return (
    <>
      <div data-testid="mobile-menu-overlay" onClick={onClose}
        className={`lg:hidden fixed inset-0 z-50 bg-slate-900/40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} />
      <aside data-testid="mobile-menu-drawer" data-open={open}
        className={`glass-panel lg:hidden fixed top-2 bottom-2 left-2 z-50 w-72 max-w-[85vw] rounded-2xl flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-[110%]"}`}>
        <button data-testid="mobile-menu-close" aria-label="Menüyü kapat" onClick={onClose} className="absolute top-4 right-3 p-2 rounded-lg hover:bg-slate-100/80 z-10">
          <X className="w-5 h-5 text-slate-700" />
        </button>
        <SidebarContent mobile onNavigate={onClose} />
      </aside>
    </>
  );
}

function OfficeInfo({ mobile }) {
  const { office, subscription } = useAuth();
  if (!office) return null;
  return (
    <div className="mt-4 rounded-xl bg-white/60 border border-white/80 px-3 py-2.5 text-xs text-slate-600">
      <div className="font-semibold text-slate-900 truncate">{office.name}</div>
      {office.office_code && <div className="text-slate-500 font-mono text-[11px]">{office.office_code}</div>}
      {subscription && (
        <div className="mt-1.5 flex items-center gap-1.5">
          <Badge data-testid={mobile ? "m-sidebar-package-badge" : "sidebar-package-badge"} variant="outline" className={`text-[10px] ${PACKAGE_STYLE[subscription.package_code]}`}>
            {subscription.package_name}
          </Badge>
          <span data-testid={mobile ? "m-sidebar-days-left" : "sidebar-days-left"} className={`text-[11px] ${subscription.status === "ACTIVE" ? "text-slate-500" : "text-red-600 font-semibold"}`}>
            {subscription.status === "FROZEN" ? "Donduruldu" : `${subscription.days_left} gün kaldı`}
          </span>
        </div>
      )}
    </div>
  );
}

function SidebarContent({ mobile, onNavigate, collapsed, onToggle }) {
  const { user, logout, hasFeature, hasPerm } = useAuth();
  const [editing, setEditing] = useState(false);
  const allowed = MENU.filter((m) => m.roles.includes(user.role) && (!m.perm || hasPerm(m.perm)));
  const items = applyPrefs(allowed, user.menu_prefs).filter((m) => !(user.menu_prefs?.hidden || []).includes(m.to));
  const tid = (to) => (mobile ? `m-${testId(to)}` : testId(to));

  return (
    <>
      <div className={`border-b border-slate-200/60 ${collapsed ? "px-3 py-4" : "p-5"}`}>
        <div className={`flex items-center gap-2 ${collapsed ? "flex-col" : ""}`}>
          <Brand compact={collapsed} />
          {onToggle && (
            <button data-testid="sidebar-collapse-toggle" onClick={onToggle} aria-label={collapsed ? "Menüyü genişlet" : "Menüyü daralt"}
              className={`${collapsed ? "mt-2" : "ml-auto"} p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-white/70 transition-colors`}>
              {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          )}
        </div>
        {!collapsed && <OfficeInfo mobile={mobile} />}
      </div>

      <nav className={`flex-1 space-y-0.5 overflow-y-auto overflow-x-hidden sidebar-scroll ${collapsed ? "p-2" : "p-3"}`}>
        {items.map((m) => {
          const isLocked = m.feature && !hasFeature(m.feature);
          return (
            <NavLink key={m.to} to={m.to} end={m.to === "/panel"} data-testid={tid(m.to)} onClick={onNavigate} title={collapsed ? m.label : undefined}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-xl text-sm font-medium transition-colors duration-200 border ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"} ${
                  isActive ? "bg-emerald-50/90 text-emerald-800 border-emerald-200/70 font-semibold shadow-sm"
                    : isLocked ? "text-slate-400 border-transparent hover:bg-white/60" : "text-slate-600 border-transparent hover:bg-white/70 hover:text-slate-900"
                }`}>
              <m.icon className="w-[18px] h-[18px] shrink-0" />
              {!collapsed && <span className="flex-1 truncate">{m.label}</span>}
              {!collapsed && isLocked && <Lock className="w-3.5 h-3.5" data-testid={`${tid(m.to)}-locked`} />}
            </NavLink>
          );
        })}
      </nav>
      <MenuEditor open={editing} onClose={() => setEditing(false)} items={allowed} />

      <div className={`border-t border-slate-200/60 ${collapsed ? "p-2" : "p-4"}`}>
        {!collapsed && (
          <div className="mb-3">
            <div className="text-sm font-semibold text-slate-900 truncate">{user.full_name}</div>
            <Badge variant="outline" className={`mt-1 text-[10px] ${ROLE_COLOR[user.role]}`}>
              <Shield className="w-3 h-3 mr-1" /> {ROLE_LABEL[user.role]}
            </Badge>
          </div>
        )}
        <button data-testid={mobile ? "m-menu-edit-btn" : "menu-edit-btn"} onClick={() => setEditing(true)} title={collapsed ? "Menüyü düzenle" : undefined}
          className={`w-full mb-2 flex items-center gap-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-white/70 hover:text-slate-900 py-2 transition-colors ${collapsed ? "justify-center" : "px-3"}`}>
          <SlidersHorizontal className="w-4 h-4" />{!collapsed && "Menüyü düzenle"}
        </button>
        <button data-testid={mobile ? "m-logout-btn" : "logout-btn"} onClick={logout} title={collapsed ? "Çıkış Yap" : undefined}
          className={`w-full flex items-center gap-2 rounded-xl border border-slate-200/80 bg-white/60 hover:bg-white text-sm font-medium text-slate-700 py-2 transition-colors ${collapsed ? "justify-center" : "px-3"}`}>
          <LogOut className="w-4 h-4" />{!collapsed && "Çıkış Yap"}
        </button>
      </div>
    </>
  );
}
